import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * One scroll rule for the whole app, keyed on the location (issues 1275, 1276, 1280).
 *
 * - A link to a new page (PUSH or REPLACE to another path) opens at its top.
 * - Back and Forward (POP) return to where the reader was on that entry. The
 *   browser's own restore runs before a lazily rendered page (the guides) is
 *   tall enough, so it lands short (issue 1275): the position each entry had
 *   when it was left is kept here, and put back once the page can hold it.
 *   An entry with nothing kept (one from before a reload) is left to the
 *   browser. A list restores its own (`useListNav`), and the hold yields to it.
 * - A `#anchor` scrolls to its element below the sticky header, waiting for a
 *   lazily loaded page to render it; this also covers a first load or reload.
 *   Cards above it that load later push it down (the campaign page's NPCs,
 *   issue 1280), so it is held at the anchor while the page settles.
 * - A change of only the query on the same path (a filter, a page of results)
 *   keeps the position.
 *
 * A hold ends when the reader scrolls, clicks, presses a key that scrolls or
 * moves focus (Tab, Shift+Tab), or focus moves at all (the browser scrolls a
 * focused element into view) — they are never pulled back —, on the next
 * navigation, or after HOLD_MS. Any
 * other key leaves it: keyboard Back (Alt+Left) reaches the page as a keydown
 * and once let the hold go before the guide was tall enough (issue 1275).
 */
export const HOLD_MS = 8000;
const TICK_MS = 100;
const USER_INPUT = ['wheel', 'touchmove', 'keydown', 'pointerdown', 'mousedown', 'focusin'];
// Tab and Shift+Tab move focus, and the browser scrolls the focused element into view: they end the hold too.
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar', 'Tab']);

/** Whether this input is the reader moving the page themselves. */
export function isUserScroll(e) {
  // A focus change (Tab, a script, an access key) brings its element into view; keyboard Back moves no focus.
  if (e.type !== 'keydown') return true;
  if (e.altKey || e.ctrlKey || e.metaKey) return false;
  return SCROLL_KEYS.has(e.key);
}

export function anchorTop(hash) {
  const id = decodeURIComponent(String(hash || '').replace(/^#/, ''));
  if (!id) return null;
  const el = document.getElementById(id);
  if (!el) return null;
  const header = document.querySelector('header.header');
  const offset = (header ? header.getBoundingClientRect().height : 0) + 8;
  return Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY - offset));
}

export function scrollToAnchor(hash) {
  const top = anchorTop(hash);
  if (top === null) return false;
  window.scrollTo(0, top);
  return true;
}

/**
 * Keep the window at `target()` (a y, or null while there is nothing to aim
 * at yet) until the reader takes over or `ms` pass. Returns the stop function.
 */
export function holdScroll(target, ms = HOLD_MS) {
  let stopped = false;
  let timer = null;
  const start = Date.now();
  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.clearInterval(timer);
    USER_INPUT.forEach((t) => window.removeEventListener(t, onInput, true));
  };
  const onInput = (e) => { if (isUserScroll(e)) stop(); };
  const apply = () => {
    if (stopped) return;
    if (Date.now() - start > ms) { stop(); return; }
    const y = target();
    if (y === null || y === undefined) return;
    // A page not yet tall enough clamps the scroll short; the next tick tries again.
    if (Math.abs(window.scrollY - y) > 2) window.scrollTo(0, y);
  };
  USER_INPUT.forEach((t) => window.addEventListener(t, onInput, { capture: true, passive: true }));
  apply();
  timer = window.setInterval(apply, TICK_MS);
  return stop;
}

// The Back/Forward hold in progress, so a page that restores its own position can take over from it.
let popHold = null;

/**
 * A page with its own scroll restore (`useListNav` for the lists) calls this as it restores: the Back/Forward hold
 * stops, so the two never pull against each other (a list's Back once landed where the hold had last saved).
 */
export function yieldScroll() {
  if (popHold) { popHold(); popHold = null; }
}

export default function ScrollOnNavigate() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(null);
  const first = useRef(true);
  // Where the reader was on each history entry, by the router's location key.
  const positions = useRef(new Map());
  const currentKey = useRef(location.key);

  // Where this entry was left, read in the same commit that shows it. The
  // browser's own restore lands short and reports that scroll before the
  // effect below runs; read there, the short position had replaced the kept
  // one and Back stayed near the top (issue 1275).
  const arrivedAt = useRef(undefined);

  // The key moves to the new entry in the same commit that shows it, before
  // the browser can report any scroll of the new page against the old entry.
  useLayoutEffect(() => {
    currentKey.current = location.key;
    arrivedAt.current = positions.current.get(location.key);
  }, [location.key]);

  useEffect(() => {
    const onScroll = () => { positions.current.set(currentKey.current, window.scrollY); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const wasPath = prevPath.current;
    prevPath.current = location.pathname;
    const initial = first.current;
    first.current = false;

    if (navType === 'POP' && !initial) {
      const saved = arrivedAt.current;
      if (saved === undefined) return undefined;
      const stop = holdScroll(() => saved);
      popHold = stop;
      return () => { stop(); if (popHold === stop) popHold = null; };
    }

    if (location.hash) {
      const hash = location.hash;
      return holdScroll(() => anchorTop(hash));
    }
    if (!initial && wasPath !== location.pathname) window.scrollTo(0, 0);
    return undefined;
  }, [location.pathname, location.search, location.hash, location.key, navType]);

  return null;
}
